import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Textarea } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { FlagBadges } from "@/components/qa/FlagBadge";
import { useKeyboard } from "@/hooks/useKeyboard";
import { useReference } from "@/hooks/useReference";
import { useToast } from "@/hooks/useToast";
import { isolates as isolatesApi } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { AuditEntry, Isolate } from "@/lib/types";

/** One isolate, with its edit history (docs/08 §8.4, C-05). */
export function IsolateDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const { organismName, wardName, specimenName, antibiotics } = useReference();

  const [isolate, setIsolate] = useState<Isolate | null>(null);
  const [history, setHistory] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [row, entries] = await Promise.all([
        isolatesApi.get(id),
        isolatesApi.history(id).catch(() => [] as AuditEntry[]),
      ]);
      setIsolate(row);
      setHistory(entries);
    } catch {
      notify(strings.errors.notFound, "error");
    } finally {
      setLoading(false);
    }
  }, [id, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  useKeyboard([
    { key: "v", description: "Void", handler: () => setVoidOpen(true) },
    { key: "Escape", description: "Back", handler: () => navigate(-1) },
  ]);

  const doVoid = async () => {
    setVoiding(true);
    try {
      await isolatesApi.void(id, voidReason.trim());
      notify("Isolate voided. Nothing was deleted.", "success");
      setVoidOpen(false);
      await load();
    } catch {
      notify(strings.errors.forbidden, "error");
    } finally {
      setVoiding(false);
    }
  };

  if (loading) return <Spinner label="Loading the isolate" />;
  if (!isolate) return <EmptyState title={strings.errors.notFound} />;

  const testedDrugs = antibiotics.filter(
    (drug) => isolate.ast_results[drug.code] != null,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-mono text-xl font-semibold text-slate-900">
            {isolate.record_id}
          </h1>
          <p className="text-sm text-slate-500">
            Entered by {isolate.author_name ?? "unknown"} on{" "}
            {formatDateTime(isolate.created_at)}
            {isolate.entered_offline && " · entered offline"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={isolate.status} />
          <FlagBadges flags={isolate.qa_flags} />
          {isolate.can_edit && isolate.status !== "void" && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/entry?edit=${isolate.id}`)}
            >
              {isolate.status === "draft" ? "Edit Draft" : "Edit"}
            </Button>
          )}
          {isolate.status !== "void" && (
            <Button variant="danger" size="sm" onClick={() => setVoidOpen(true)}>
              Void
            </Button>
          )}
        </div>
      </div>

      <Tabs.Root defaultValue="detail">
        <Tabs.List className="flex gap-1 border-b border-slate-200">
          {(
            [
              ["detail", "Detail"],
              ["history", `History (${history.length})`],
              ["flags", `QA flags (${isolate.qa_flags.length})`],
            ] as const
          ).map(([value, label]) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="border-b-2 border-transparent px-3 py-2 text-sm font-medium text-slate-600
                         data-[state=active]:border-brand-700 data-[state=active]:text-brand-900"
            >
              {label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="detail" className="pt-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title={strings.entry.specimenCard} />
              <CardBody>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <Detail label={strings.entry.specimenDate} value={formatDate(isolate.specimen_date)} />
                  <Detail label={strings.entry.labNumber} value={isolate.lab_number} />
                  <Detail label={strings.entry.hospitalNumber} value={isolate.hospital_number} />
                  <Detail
                    label={strings.entry.age}
                    value={
                      isolate.age_value === null
                        ? null
                        : `${isolate.age_value} ${isolate.age_unit ?? ""}`
                    }
                  />
                  <Detail label={strings.entry.sex} value={isolate.sex} />
                  <Detail label={strings.entry.ward} value={isolate.ward_raw ?? wardName(isolate.ward_code)} />
                  <Detail label={strings.entry.specimenType} value={specimenName(isolate.specimen_type)} />
                  <Detail label={strings.entry.cultureResult} value={isolate.culture_result?.replace(/_/g, " ")} />
                </dl>
              </CardBody>
            </Card>

            <Card>
              <CardHeader title={strings.entry.organismCard} />
              <CardBody>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <Detail
                    label={strings.entry.organism}
                    value={
                      isolate.organism_code === "other"
                        ? isolate.organism_as_written
                        : organismName(isolate.organism_code)
                    }
                  />
                  <Detail label={strings.entry.isolateNumber} value={isolate.isolate_number} />
                </dl>

                <h3 className="mt-4 text-sm font-medium text-slate-700">
                  {strings.entry.antibiotics}
                </h3>
                {testedDrugs.length === 0 ? (
                  <p className="mt-1 text-sm text-slate-500">Nothing was tested.</p>
                ) : (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {testedDrugs.map((drug) => {
                      const value = isolate.ast_results[drug.code];
                      return (
                        <li key={drug.code}>
                          <Badge
                            tone={value === "R" ? "blocking" : value === "I" ? "warning" : "ok"}
                          >
                            {drug.code} {String(value)}
                          </Badge>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>
          </div>

          {isolate.notes && (
            <Card className="mt-4">
              <CardHeader title={strings.entry.notes} />
              <CardBody>
                <p className="whitespace-pre-wrap text-sm text-slate-700">{isolate.notes}</p>
              </CardBody>
            </Card>
          )}
        </Tabs.Content>

        <Tabs.Content value="history" className="pt-4">
          <Card>
            {history.length === 0 ? (
              <EmptyState title="No history recorded." />
            ) : (
              <Table caption="Edit history">
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Who</Th>
                    <Th>Action</Th>
                    <Th>What changed</Th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((entry) => (
                    <tr key={entry.id}>
                      <Td className="whitespace-nowrap text-xs">
                        {formatDateTime(entry.occurred_at)}
                      </Td>
                      <Td>
                        {entry.actor_name ?? "—"}
                        {entry.actor_role && (
                          <span className="ml-1 text-xs text-slate-500">
                            ({entry.actor_role.replace(/_/g, " ")})
                          </span>
                        )}
                      </Td>
                      <Td>{entry.action.replace(/_/g, " ")}</Td>
                      <Td>
                        {entry.changes ? (
                          <ul className="space-y-0.5 text-xs">
                            {Object.entries(entry.changes).map(([field, change]) => (
                              <li key={field}>
                                <span className="font-medium">{field}</span>:{" "}
                                <span className="text-red-700 line-through">
                                  {JSON.stringify(change.old)}
                                </span>{" "}
                                &rarr;{" "}
                                <span className="text-green-700">
                                  {JSON.stringify(change.new)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          "—"
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </Tabs.Content>

        <Tabs.Content value="flags" className="pt-4">
          <Card>
            {isolate.qa_flags.length === 0 ? (
              <EmptyState title={strings.qa.noFlags} />
            ) : (
              <Table caption="QA flags">
                <thead>
                  <tr>
                    <Th>Rule</Th>
                    <Th>Severity</Th>
                    <Th>Message</Th>
                    <Th>Resolved</Th>
                  </tr>
                </thead>
                <tbody>
                  {isolate.qa_flags.map((flag) => (
                    <tr key={flag.id}>
                      <Td className="font-mono text-xs">{flag.code}</Td>
                      <Td>{flag.severity}</Td>
                      <Td>{flag.message}</Td>
                      <Td className="text-xs">
                        {flag.resolved_at ? (
                          <>
                            {formatDateTime(flag.resolved_at)}
                            <p className="text-slate-500">{flag.resolution_reason}</p>
                          </>
                        ) : (
                          <Badge tone="warning">Open</Badge>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </Tabs.Content>
      </Tabs.Root>

      <Dialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void this isolate"
        description="Nothing is deleted. The row stays in the database with a void status and this reason is recorded in the audit log."
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoidOpen(false)}>
              {strings.common.cancel}
            </Button>
            <Button
              variant="danger"
              loading={voiding}
              disabled={voidReason.trim().length < 3}
              onClick={() => void doVoid()}
            >
              Void
            </Button>
          </>
        }
      >
        <Field id="void-reason" label="Why are you voiding it?" required>
          <Textarea
            id="void-reason"
            value={voidReason}
            onChange={(event) => setVoidReason(event.target.value)}
            placeholder="Duplicate of T1-0042."
          />
        </Field>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900">{value || "—"}</dd>
    </>
  );
}
